import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocationModel } from '../../models/location.model';
import { SettingsModel } from '../../models/setting.model';
import { PropertyListModel } from '../../models/property-list.model';
import { AppService } from '../../services/app.service';
import { SearchCriteriaModel } from '../../models/search-criteria.model';
import { SearchService } from '../../services/search.service';
import { SearchResultModel } from '../../models/search-result.model';
import { BoundCoordinates } from '../../models/map.model';
import { MatButtonModule } from '@angular/material/button';
import { PropertyMapModel } from '../../models/property.map.model';
import { PropertyMapCluster } from '../../models/property-map-cluster.model';
import { PropertyListPropertyComponent } from '../property-list-property/property-list-property.component';
import { PropertyListComponent } from '../property-list/property-list.component';
import { SettingsService } from '../../services/settings.service';

declare const google: any;

@Component({
  selector: 'app-search-map',
  standalone: true,
  imports: [CommonModule, MatButtonModule, PropertyListComponent],
  templateUrl: './search-map.component.html',
  styleUrls: ['./search-map.component.scss'],
})
export class SearchMapComponent implements OnInit {
  @Input() settings: SettingsModel = new SettingsModel();
  @Input() location: LocationModel = new LocationModel();
  @Input() mapFullWidth = false;

  searchService = inject(SearchService);

  boundCoordinates: BoundCoordinates = new BoundCoordinates();
  searchCriteria = this.searchService.clientSearchCriteria();
  searchResult: SearchResultModel = new SearchResultModel();
  properties: PropertyMapModel[] = [];
  selectedProperty: PropertyListModel = new PropertyListModel();
  selectedProperties: PropertyListModel[] = [];

  propertyMapCluster: PropertyMapCluster[] = [];

  initialLoad: boolean = true;

  isMobile = true;

  loading = true;
  freezeMap = false;

  mapOptions = {};

  mapCenter = { lat: -33.8688, lng: 151.2093 };
  mapZoom = 10;
  markers: google.maps.marker.AdvancedMarkerElement[] = [];

  defaultMarkerIcon = '/assets/icons/marker-marked.png';

  map: any;

  infoWindow: any;

  @ViewChild('infoWindow') infoWindowRef!: ElementRef;

  updateMapProperties = true;

  ignoreNextIdleEvent = false;

  @ViewChild(PropertyListPropertyComponent)
  propertyResults?: PropertyListPropertyComponent;

  @ViewChild('propertyComponent', { static: false })
  propertyComponent?: ElementRef;

  @ViewChild('propertyResults', { static: false }) mapInfoWindow:
    | google.maps.InfoWindow
    | undefined;

  constructor(
    private appService: AppService,
    private settingsService: SettingsService,
  ) {
    effect(() => {
      this.searchService.updateMapProperties();

      if (
        this.searchService.updateMapProperties() != this.updateMapProperties
      ) {
        this.updateMapProperties = this.searchService.updateMapProperties();
      }

      this.propertyMapCluster = this.searchService.mapProperties() ?? [];

      this.initMapCluster();
    });
    effect(() => {
      this.selectedProperties = this.searchService.selectedProperty() ?? [];

      this.searchService.updatePropertyList();

      if (!this.ignoreNextIdleEvent) this.getProperties('effect');
    });
  }

  ngOnInit(): void {
    this.mapOptions = {
      zoom: 10,
      disableDefaultUI: true,
      zoomControl: true,
      maxZoom: 19,
      minZoom: 4,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapId: 'e85b346110bedded',
    };
    this.isMobile = this.settingsService.getMobileStatus();
    this.boundCoordinates = this.searchCriteria.Bounderies;

    this.infoWindow = new google.maps.InfoWindow();

    this.mapFullWidth = this.searchService.mapFullWidth();
  }

  fitBounds() {
    let bounds = new google.maps.LatLngBounds();
    bounds.extend({
      lat:
        Math.round(this.searchCriteria.Bounderies.SouthWest.Latitude) == 0
          ? this.location.GeoBounds.North
          : this.searchCriteria.Bounderies.SouthWest.Latitude,
      lng:
        Math.round(this.searchCriteria.Bounderies.SouthWest.Longitude) == 0
          ? this.location.GeoBounds.West
          : this.searchCriteria.Bounderies.SouthWest.Longitude,
    });

    bounds.extend({
      lat:
        Math.round(this.searchCriteria.Bounderies.NorthEast.Latitude) == 0
          ? this.location.GeoBounds.South
          : this.searchCriteria.Bounderies.NorthEast.Latitude,
      lng:
        Math.round(this.searchCriteria.Bounderies.NorthEast.Longitude) == 0
          ? this.location.GeoBounds.East
          : this.searchCriteria.Bounderies.NorthEast.Longitude,
    });

    this.map?.fitBounds(bounds);

    this.mapZoom = this.map?.getZoom() ?? 0;
    this.searchCriteria.ZoomLevel = this.map?.getZoom() ?? 0;

    return bounds;
  }

  openInfoWindow(marker: google.maps.marker.AdvancedMarkerElement) {
    const position = marker.position;

    if (!position) return;

    this.freezeMap = true;
    this.ignoreNextIdleEvent = true;

    this.appService
      .getData(
        `Hotels/ByCoordinates?longitude=${position.lng}&latitude=${position.lat}`,
      )
      .then((data: string) => {
        this.selectedProperties = JSON.parse(data);

        setTimeout(() => {
          const map = this.map;

          if (this.propertyComponent?.nativeElement) {
            let content = this.propertyComponent.nativeElement.cloneNode(true);

            content.style.display = 'block';
            this.infoWindow.setContent(content);

            this.infoWindow.open({
              anchor: marker,
              map,
            });

            google.maps.event.addListener(this.infoWindow, 'closeclick', () => {
              this.selectedProperties = [];
              this.freezeMap = false;
            });
          }
        }, 0);
      });
  }

  getCluseringIcon(count: number) {
    if (count < 50) {
      return '/assets/icons/clustering/m2-2.png';
    } else if (count < 1000) {
      return '/assets/icons/clustering/m3-2.png';
    } else if (count < 10000) {
      return '/assets/icons/clustering/m4-2.png';
    }
    return '/assets/icons/clustering/m5-2.png';
  }

  getProperties(from: string) {
    this.ignoreNextIdleEvent = true;
    this.searchCriteria = this.searchService.clientSearchCriteria();
    this.loading = true;

    this.initMapCluster();
  }

  onIdle() {
    if (this.ignoreNextIdleEvent) {
      this.ignoreNextIdleEvent = false;
    } else {
      this.updateBounds('onIdle');
    }

    this.ignoreNextIdleEvent = false;
  }

  initMapCluster() {
    this.freezeMap = true;

    if (this.initialLoad) {
      this.map = new google.maps.Map(
        document.getElementById('map') as HTMLElement,
        this.mapOptions,
      );

      this.map.addListener('idle', () => {
        this.onIdle();
      });

      this.fitBounds();
    }

    this.initialLoad = false;

    for (let i = 0; i < this.markers.length; i++) {
      this.markers[i].map = null;
    }

    this.markers = [];

    for (let data of this.propertyMapCluster) {
      const position = new google.maps.LatLng(
        data.Geometry.Coordinates[1],
        data.Geometry.Coordinates[0],
      );

      if (data.Properties && data.Properties.Cluster) {
        const clusterContent = document.createElement('div');
        let clusterIconSizeBasedOnCount = 0;

        if (data.Properties.Point_count < 50) {
          clusterIconSizeBasedOnCount = 60;
        } else if (data.Properties.Point_count < 1000) {
          clusterIconSizeBasedOnCount = 70;
        } else if (data.Properties.Point_count < 10000) {
          clusterIconSizeBasedOnCount = 80;
        } else {
          clusterIconSizeBasedOnCount = 60;
        }

        clusterContent.innerHTML = `
          <div class="cluster-marker" style="background-image: url('${this.getCluseringIcon(data.Properties.Point_count)}'); width: ${clusterIconSizeBasedOnCount}px; height: ${clusterIconSizeBasedOnCount}px;">
            <span class="cluster-count">${data.Properties.Point_count}</span>
          </div>
        `;
        const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
          position,
          map: this.map,
          content: clusterContent,
        });

        advancedMarker.addListener('click', () => {
          const currentZoomLevel = this.map.getZoom();
          this.map.setCenter(position);
          this.map.setZoom(currentZoomLevel + 2);
        });

        this.markers.push(advancedMarker);
      } else {
        const markerContent = document.createElement('div');
        markerContent.innerHTML = `
          <div class="custom-marker">
            <img src="${this.defaultMarkerIcon}" alt="Marker Icon" />
          </div>
        `;
        const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
          position,
          map: this.map,
          content: markerContent,
        });

        advancedMarker.addListener('click', () => {
          this.openInfoWindow(advancedMarker);
        });

        this.markers.push(advancedMarker);
      }
    }

    google.maps.event.trigger(this.map, 'resize');
    this.freezeMap = false;
    this.ignoreNextIdleEvent = false;
  }

  updateBounds(from: string) {
    const neLat = this.map?.getBounds()?.getNorthEast()?.lat();
    const swLat = this.map?.getBounds()?.getSouthWest()?.lat();
    const neLng = this.map?.getBounds()?.getNorthEast()?.lng();
    const swLng = this.map?.getBounds()?.getSouthWest()?.lng();
    const zoomLevel = this.map?.getZoom();

    this.mapZoom = zoomLevel ?? 0;

    if (
      zoomLevel != this.searchCriteria.ZoomLevel ||
      (neLat && swLat && neLng && swLng)
    ) {
      if (
        this.boundCoordinates.NorthEast.Latitude === +neLat.toFixed(4) &&
        this.boundCoordinates.NorthEast.Longitude === +neLng.toFixed(4) &&
        this.boundCoordinates.SouthWest.Latitude === +swLat.toFixed(4) &&
        this.boundCoordinates.SouthWest.Longitude === +swLng.toFixed(4)
      ) {
        return;
      }

      this.boundCoordinates.NorthEast.Latitude = +neLat.toFixed(4);
      this.boundCoordinates.NorthEast.Longitude = +neLng.toFixed(4);
      this.boundCoordinates.SouthWest.Latitude = +swLat.toFixed(4);
      this.boundCoordinates.SouthWest.Longitude = +swLng.toFixed(4);

      let searchCriteria = new SearchCriteriaModel();
      this.searchCriteria.Bounderies = this.boundCoordinates;
      this.searchCriteria.ZoomLevel = this.mapZoom;

      this.searchCriteria.ZoomLevel = zoomLevel;

      this.searchCriteria.PageSize =
        this.searchCriteria.PageSize *
        (this.searchCriteria.Page > 0 ? this.searchCriteria.Page : 1);
      this.searchCriteria.Page = 1;

      this.searchService.setClientSearchCriteria(this.searchCriteria, 'map');

      this.getProperties('updateBounds');
    }
  }

  resetMap() {
    this.boundCoordinates = new BoundCoordinates();
    this.searchCriteria.Bounderies = new BoundCoordinates();

    this.searchService.setClientSearchCriteria(this.searchCriteria, 'map');

    this.fitBounds();
  }

  changeMapFullWidth() {
    this.mapFullWidth = !this.mapFullWidth;
    this.searchService.mapFullWidth.set(this.mapFullWidth);
  }
}
